import { useState, useEffect } from "react";
import type { FC } from "react";
import {
  getBotDetails,
  getBotStats,
  deleteBot,
  getAppointments,
} from "../api/client";
import type { BotDetails, BotStats } from "../api/client";
import { LoadingSpinner, ErrorMessage } from "../components/Common";

interface Props {
  user: ReturnType<typeof import("../hooks/useTelegram").useTelegram>["user"];
  botId: string;
  onBack: () => void;
}

type Tab = "overview" | "stats" | "settings";

export const BotDetailPage: FC<Props> = ({ user, botId, onBack }) => {
  const [tab, setTab] = useState<Tab>("overview");
  const [bot, setBot] = useState<BotDetails | null>(null);
  const [stats, setStats] = useState<BotStats | null>(null);
  const [appointments, setAppointments] = useState<Array<{
    id: string; startTime: string; endTime: string;
    serviceId: string; status: string;
    customerName: string; customerPhone: string;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    Promise.all([
      getBotDetails(botId),
      getBotStats(botId),
      getAppointments(botId, user?.id ?? 0).catch(() => []),
    ])
      .then(([b, s, a]) => {
        setBot(b);
        setStats(s);
        setAppointments(a);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Xatolik"))
      .finally(() => setLoading(false));
  }, [botId, user?.id]);

  const handleDelete = async () => {
    if (!confirm("Botni o'chirishni istaysizmi? Bu amalni qaytarib bo'lmaydi.")) return;
    try {
      await deleteBot(botId, user?.id ?? 0);
      onBack();
    } catch (e) {
      setError(e instanceof Error ? e.message : "O'chirishda xatolik");
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} onRetry={() => window.location.reload()} />;
  if (!bot) return <ErrorMessage message="Bot topilmadi" onRetry={onBack} />;

  const config = bot.config as Record<string, unknown>;
  const modules = (config.enabledModules as string[]) ?? [];
  const info = (config.info ?? {}) as Record<string, string>;
  const booking = (config.booking ?? {}) as Record<string, unknown>;
  const aiCfg = (config.ai ?? {}) as Record<string, string | number | undefined>;

  return (
    <div className="detail-page">
      <header className="page-header">
        <button onClick={onBack} className="back-btn">← Botlar</button>
        <h1>{config.businessName as string}</h1>
      </header>

      <div className="tab-bar">
        {(["overview", "stats", "settings"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`tab ${tab === t ? "active" : ""}`}
          >
            {t === "overview" ? "Umumiy" : t === "stats" ? "Statistika" : "Sozlamalar"}
          </button>
        ))}
      </div>

      {/* ── Overview Tab ──────────────────────────────────── */}
      {tab === "overview" && (
        <div className="tab-content">
          <div className="bot-info-header">
            <p className="hint-text">@{bot.botUsername}</p>
            <span className={`badge ${bot.status === "active" ? "badge-success" : "badge-warning"}`}>
              {bot.status === "active" ? "Faol" : bot.status}
            </span>
          </div>

          {/* Stats cards */}
          {stats && (
            <div className="overview-section">
              <div className="stat-card">
                <span className="stat-value">{stats.todayAppointments}</span>
                <span className="stat-label">Bugungi bandlik</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{stats.totalCustomers}</span>
                <span className="stat-label">Mijozlar</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{stats.aiCallsThisMonth}</span>
                <span className="stat-label">Bu oy AI chaqiruv</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{modules.length}</span>
                <span className="stat-label">Modullar</span>
              </div>
            </div>
          )}

          {/* Business info */}
          {Object.keys(info).length > 0 && (
            <div className="card" style={{ marginTop: 16 }}>
              <h3>Biznes ma'lumoti</h3>
              {info.address && <p className="hint-text">Manzil: {info.address}</p>}
              {info.workingHours && <p className="hint-text">Ish vaqti: {info.workingHours}</p>}
              {info.phone && <p className="hint-text">Telefon: {info.phone}</p>}
            </div>
          )}

          {/* Services */}
          {booking && Array.isArray(booking.services) && (booking.services as Array<{name: string; price: number}>).length > 0 && (
            <div className="card">
              <h3>Xizmatlar</h3>
              <div className="modules-list">
                {(booking.services as Array<{name: string; price: number}>).map((s, i) => (
                  <div key={i} className="module-card">
                    <span className="module-name">{s.name}</span>
                    <span className="module-detail">
                      {s.price ? `${s.price.toLocaleString()} so'm` : "Narx ko'rsatilmagan"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Enabled modules */}
          <div className="card">
            <h3>Faol modullar</h3>
            <div className="modules-list">
              {modules.map((m) => (
                <div key={m} className="module-card">
                  <span className="module-name">{m}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Stats Tab ─────────────────────────────────────── */}
      {tab === "stats" && stats && (
        <div className="tab-content">
          <div className="card">
            <h3>Statistika</h3>
            <div className="detail-row">
              <span>Bugungi bandlar</span>
              <strong>{stats.todayAppointments}</strong>
            </div>
            <div className="detail-row">
              <span>Jami mijozlar</span>
              <strong>{stats.totalCustomers}</strong>
            </div>
            <div className="detail-row">
              <span>Bu oy AI chaqiruv</span>
              <strong>{stats.aiCallsThisMonth}</strong>
            </div>
          </div>

          {/* Recent appointments */}
          {appointments.length > 0 && (
            <div className="card">
              <h3>Oxirgi bandlar</h3>
              {appointments.slice(0, 10).map((a) => (
                <div key={a.id} className="appointment-row">
                  <div>
                    <strong>{a.customerName || "Mijoz"}</strong>
                    <p className="hint-text">{a.customerPhone}</p>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <p>{new Date(a.startTime).toLocaleDateString("uz-UZ")}</p>
                    <span className={`badge ${a.status === "confirmed" ? "badge-success" : "badge-warning"}`}>
                      {a.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Settings Tab ──────────────────────────────────── */}
      {tab === "settings" && (
        <div className="tab-content">
          <div className="info-card">
            <h4>Bot ma'lumoti</h4>
            <p>ID: {bot.id}</p>
            <p>Username: @{bot.botUsername}</p>
            <p>Holat: {bot.status}</p>
            <p>Yaratilgan: {new Date(bot.createdAt).toLocaleDateString("uz-UZ")}</p>
          </div>

          {aiCfg && Object.keys(aiCfg).length > 0 && (
            <div className="info-card">
              <h4>AI sozlamalari</h4>
              {aiCfg.systemPrompt && (
                <details>
                  <summary>System prompt</summary>
                  <pre className="knowledge-text">{aiCfg.systemPrompt as string}</pre>
                </details>
              )}
              {aiCfg.knowledge && (
                <details>
                  <summary>Bilim bazasi</summary>
                  <pre className="knowledge-text">{aiCfg.knowledge as string}</pre>
                </details>
              )}
              {aiCfg.dailyLimit && <p>Kunlik limit: {aiCfg.dailyLimit as number}</p>}
            </div>
          )}

          <div className="card" style={{ background: "#fff0f0", border: "1px solid #ffd0d0" }}>
            <h4 style={{ color: "var(--tg-destructive)", marginBottom: 8 }}>Xavfli hudud</h4>
            <p className="hint-text" style={{ marginBottom: 12 }}>
              Botni o'chirish — bu amalni qaytarib bo'lmaydi. Barcha mijozlar va sozlamalar o'chadi.
            </p>
            <button onClick={handleDelete} className="btn" style={{ background: "var(--tg-destructive)", color: "white", width: "100%" }}>
              Botni o'chirish
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
